import React, { useEffect, useMemo, useState } from 'react';
import Select, { ValueType } from 'react-select';
import { UserType } from '../../../enums/user-type.enum';
import { UserUIModel } from '../../../ui-models/user.ui-model';
import { ErrorInfo, isErrorInfo } from '../../../interfaces/error-info.interface';
import { searchUsers } from '../../../services/data/user.data';

export interface StaffOption {
  value: string;
  label: string;
}

/**
 * Staff, by the same rule the staff list uses: everyone except guests and
 * landlords.
 *
 * Mirrors `STAFF_LIST_ROLES` in `staff-roles.config.ts` rather than listing
 * roles by hand — a hand-written list silently hides accounts that exist the
 * moment a role is added, and the people missing from a picker are exactly the
 * ones somebody is trying to find. `visitor` and `online_user` are kept for the
 * same reason they are kept there.
 */
export const STAFF_USER_TYPES: UserType[] = Object.values(UserType).filter(
  (type: UserType) => type !== UserType.GUEST && type !== UserType.LANDLORD
);

const selectMenuPortalStyles = {
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 })
};

interface StaffPickerProps {
  /** The selected user's id, or empty. */
  value?: string;
  onChange: (userId: string | undefined) => void;
  placeholder?: string;
  isDisabled?: boolean;
  isClearable?: boolean;
  /** Ids to leave out — e.g. the member being edited, who cannot report to themselves. */
  excludeIds?: string[];
}

/**
 * Pick a person, not a user id.
 *
 * Every id the team and lead services store is an authenticator user id, and
 * nobody operating the admin has those to hand — the first version of these
 * screens asked for one in a text box, which made the forms unusable. This
 * searches staff by name or email and submits the id behind the scenes.
 *
 * The option value is `user.id`, which is the **Keycloak** id. That is what
 * `TeamMember._id`, a lead's `assignedTo` and an assignment rule's
 * `assigneeIds` all hold. The authenticator also exposes a Mongo `_id` on the
 * same record; using that one resolves to nobody.
 */
export const StaffPicker: React.FC<StaffPickerProps> = ({
  value,
  onChange,
  placeholder = 'Search staff by name or email',
  isDisabled = false,
  isClearable = true,
  excludeIds = []
}) => {
  const [staff, setStaff] = useState<UserUIModel[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    searchUsers({ role: STAFF_USER_TYPES }, 'StaffPicker').then(
      (response: UserUIModel[] | ErrorInfo) => {
        if (cancelled) return;
        if (!isErrorInfo(response)) setStaff(response);
        setLoading(false);
      }
    );
    return () => {
      cancelled = true;
    };
  }, []);

  const options: StaffOption[] = useMemo(
    () =>
      staff
        .filter((user: UserUIModel) => user.id && excludeIds.indexOf(user.id) < 0)
        .map((user: UserUIModel) => ({
          value: user.id as string,
          // Email is in the label, not a subtitle, so react-select's own
          // filtering matches on it — people search by whichever they know.
          label: [user.name || 'Unnamed', user.email, user.role].filter(Boolean).join(' · ')
        })),
    [staff, excludeIds]
  );

  const selected: StaffOption | null =
    options.find((option: StaffOption) => option.value === value) ??
    // A stored id whose account no longer resolves still has to render as
    // something, or editing a member would silently clear their manager.
    (value ? { value, label: value } : null);

  return (
    <Select
      isLoading={loading}
      isDisabled={isDisabled}
      isClearable={isClearable}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      styles={selectMenuPortalStyles}
      classNamePrefix="react-select"
      options={options}
      value={selected}
      placeholder={loading ? 'Loading staff…' : placeholder}
      noOptionsMessage={() => (loading ? 'Loading staff…' : 'No staff match that search')}
      onChange={(option: ValueType<StaffOption, false>) =>
        onChange(option ? (option as StaffOption).value : undefined)
      }
    />
  );
};
